import React, { useState, useEffect } from 'react';
import { portfolioAPI } from '../../services/api';
import './AboutSection.css';

interface StatsData {
  projects: number;
  solutions: number;
  clients: number;
  satisfaction: number;
}

const AboutSection: React.FC = () => {
  const [stats, setStats] = useState<StatsData>({
    projects: 0,
    solutions: 0,
    clients: 5,
    satisfaction: 100
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [projectsRes, solutionsRes] = await Promise.all([
        portfolioAPI.getProjects(),
        portfolioAPI.getSolutions()
      ]);
      
      const projects = projectsRes.data || [];
      const solutions = solutionsRes.data || [];
      
      setStats({
        projects: projects.length,
        solutions: solutions.length,
        clients: 5, // Static or could be fetched from another endpoint
        satisfaction: 98 // Static or could be fetched from another endpoint
      });
      
      setError('');
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
      // Fallback to default values
      setStats({
        projects: 5,
        solutions: 3,
        clients: 5,
        satisfaction: 100
      });
    } finally {
      setLoading(false);
    }
  };

  // SVG Icons
  const Icons = {
    Projects: () => (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    Solutions: () => (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    Clients: () => (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    Satisfaction: () => (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };

  const statsData = [
    { 
      number: loading ? '...' : `${stats.projects}+`, 
      label: 'Projects Completed', 
      icon: <Icons.Projects />
    },
    { 
      number: loading ? '...' : `${stats.solutions}+`, 
      label: 'Solutions Delivered', 
      icon: <Icons.Solutions />
    },
    { 
      number: loading ? '...' : `${stats.clients}+`, 
      label: 'Happy Clients', 
      icon: <Icons.Clients />
    },
    { 
      number: loading ? '...' : `${stats.satisfaction}%`, 
      label: 'Client Satisfaction', 
      icon: <Icons.Satisfaction />
    },
  ];

  return (
    <section id="about" className="about-section">
      <div className="about-container">
        <div className="about-grid">
          {/* Left Content */}
          <div className="about-content">
            <h2 className="about-title">About ZiTech Solutions</h2>
            <p className="about-text">
              ZiTech Solutions is a technology-driven company specializing in providing innovative
              digital solutions. We deliver our services through modern technologies, agile methodologies,
              and client-focused strategies to ensure scalability, reliability, and high performance.
            </p>
            <p className="about-text">
              Our solutions are tailored to meet the unique needs of each client, with ongoing support
              and maintenance to guarantee long-term efficiency. We serve businesses, institutions,
              startups, and individuals seeking reliable tech solutions to improve operations,
              digital presence, and service delivery.
            </p>
            <p className="about-text">
              With a strong focus on quality, innovation, and customer satisfaction, ZiTech aims to
              empower organizations through cutting-edge technology that drives growth and operational excellence.
            </p>
          </div>

          {/* Right Side - Stats - Clean no cards */}
          <div className="stats-grid">
            {statsData.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
                <div className="stat-divider"></div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="about-error">
            <p>{error}</p>
            <button onClick={fetchStats} className="about-retry-btn">Retry</button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutSection;